import { useState, useEffect, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

import { useTranslation } from 'react-i18next';
const TOUR_STEPS = [
  {
    target: "body",
    title: "Welcome to your Dashboard",
    content:
      "This is your control center. Here you manage your AI agents, monitor performance and configure automations. Let's explore the main areas?",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: "[data-tour='analytics-section']",
    title: "Analytics",
    content:
      "Track real-time metrics: messages sent, response time, automatic resolution rate and NPS. All in simple visuals for quick decisions.",
    placement: "bottom",
  },
  {
    target: "[data-tour='ab-testing-section']",
    title: "A/B Testing",
    content:
      "Create and monitor A/B tests to optimize agent performance. Compare response variations and discover which approach converts more.",
    placement: "bottom",
  },
  {
    target: "[data-tour='automations-section']",
    title: "Automations",
    content:
      "Configure automatic flows for your agents. Create triggers, conditions and actions to automate repetitive tasks and save hours of work.",
    placement: "bottom",
  },
  {
    target: "[data-tour='course-section']",
            title: "Course",
    content:
      "Learn to use Max OS Cloud with our integrated course. Step-by-step tutorials, best practices and tips to get the most out of the platform.",
    placement: "bottom",
  },
  {
    target: "[data-tour='support-section']",
    title: "Support",
    content:
      "Need help? Open a support ticket directly from the dashboard. Our team responds within 24 hours. We also have documentation and FAQ available.",
    placement: "bottom",
  },
];

const STORAGE_KEY = "maxemployee.dashboard_tour_completed";

function TourTooltip({
  step,
  index,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  isLast,
  isFirst,
}) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return (
    <div
      className="fixed z-[9999] transition-all duration-300 ease-out animate-in fade-in"
      style={{
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: isMobile ? "calc(100vw - 2rem)" : "440px",
        maxWidth: "440px",
      }}
    >
      <div className="relative bg-zinc-950 border border-zinc-700 shadow-2xl shadow-black/80 rounded-sm">
        {/* Header accent bar */}
        <div className="h-1 bg-gradient-to-r from-amber-500 to-amber-400" />

        {/* Close / skip button */}
        <button
          onClick={onSkip}
          className="absolute top-3 right-3 p-1 text-zinc-500 hover:text-white transition-colors z-10"
          aria-label={t('dashboard_tour.skip_tour')}
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6">
          {/* Step counter */}
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">
            Step {index + 1} of {totalSteps}
          </div>

          {/* Title */}
          <h3 className="font-display text-xl uppercase text-white mb-3 pr-6">
            {step.title}
          </h3>

          {/* Content */}
          <p className="text-zinc-400 text-sm leading-relaxed">
            {step.content}
          </p>

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mt-5 mb-4">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-1 transition-all duration-300 rounded-sm ${
                  i === index
                    ? "w-6 bg-amber-500"
                    : i < index
                    ? "w-3 bg-zinc-600"
                    : "w-3 bg-zinc-800"
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={onSkip}
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-300 transition-colors px-2 py-1"
            >{t('dashboard_tour.skip_tour')}</button>

            <div className="flex items-center gap-2">
              {!isFirst && (
                <button
                  onClick={onPrev}
                  className="flex items-center gap-1 border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Back
                </button>
              )}
              <button
                onClick={onNext}
                className="flex items-center gap-1 bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 font-bold uppercase tracking-wider text-[10px] transition-colors"
              >
                {isLast ? (
                  <>
                    <Sparkles className="w-3.5 h-3.5" /> Concluir
                  </>
                ) : (
                  <>
                    Proximo <ChevronRight className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DashboardTour({ onComplete }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightStyle, setHighlightStyle] = useState(null);
  const overlayRef = useRef(null);

  // Check if tour should start — only on first dashboard visit
  useEffect(() => {
    try {
      const completed = localStorage.getItem(STORAGE_KEY);
      if (completed !== "true") {
        const timer = setTimeout(() => setIsOpen(true), 600);
        return () => clearTimeout(timer);
      }
    } catch {
      // localStorage unavailable
    }
  }, []);

  // Position highlight over target element
  const updateHighlight = useCallback(() => {
    const step = TOUR_STEPS[currentStep];
    if (!step || step.placement === "center" || step.target === "body") {
      setHighlightStyle(null);
      return;
    }

    const target = document.querySelector(step.target);
    if (!target) {
      setHighlightStyle(null);
      return;
    }

    const rect = target.getBoundingClientRect();
    const padding = 8;

    setHighlightStyle({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });
  }, [currentStep]);

  useEffect(() => {
    if (isOpen) {
      updateHighlight();
      window.addEventListener("resize", updateHighlight);
      window.addEventListener("scroll", updateHighlight);
      return () => {
        window.removeEventListener("resize", updateHighlight);
        window.removeEventListener("scroll", updateHighlight);
      };
    }
  }, [isOpen, updateHighlight]);

  const closeTour = useCallback(() => {
    setIsOpen(false);
    setHighlightStyle(null);
    try {
      localStorage.setItem(STORAGE_KEY, "true");
    } catch {
      // ignore
    }
    onComplete?.();
  }, [onComplete]);

  const goNext = useCallback(() => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      closeTour();
    }
  }, [currentStep, closeTour]);

  const goPrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  const restartTour = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, "false");
    } catch {
      // ignore
    }
    setCurrentStep(0);
    setIsOpen(true);
  }, []);

  // Expose restart globally
  useEffect(() => {
    window.__restartDashboardTour = restartTour;
    return () => {
      delete window.__restartDashboardTour;
    };
  }, [restartTour]);

  if (!isOpen) return null;

  const step = TOUR_STEPS[currentStep];

  return (
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[9998] bg-black/70 transition-opacity duration-300"
        onClick={closeTour}
        style={{ pointerEvents: "auto" }}
      />

      {/* Highlight cutout */}
      {highlightStyle && (
        <div
          className="fixed z-[9998] pointer-events-none transition-all duration-300"
          style={{
            ...highlightStyle,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.7)",
            border: "2px solid rgba(245, 158, 11, 0.5)",
            borderRadius: "4px",
          }}
        />
      )}

      {/* Tooltip */}
      <TourTooltip
        step={step}
        index={currentStep}
        totalSteps={TOUR_STEPS.length}
        onNext={goNext}
        onPrev={goPrev}
        onSkip={closeTour}
        isFirst={currentStep === 0}
        isLast={currentStep === TOUR_STEPS.length - 1}
      />
    </>
  );
}

// Utility to restart tour from outside
export function restartDashboardTour() {
  if (window.__restartDashboardTour) {
    window.__restartDashboardTour();
  }
}

// Utility to check if tour was completed
export function isDashboardTourCompleted() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

// Utility to reset tour state (for testing / settings)
export function resetDashboardTour() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
